<script setup lang="ts">
// A full-screen blocking surface over the whole app — the v4 form of v3's `fixed inset-0`
// onboarding overlay (`OnboardingModal.vue:3-6`). Built on the same Headless UI Dialog as `Modal`,
// so focus is trapped and the page behind is inert, and registered on the overlay stack as a
// `modal`, so a dialog opened from inside it (a connector modal, a confirm) stacks above it.
//
// `dismissible: false` is the point of the primitive: Escape and outside presses do nothing, and
// there is no close control — the caller decides when, and whether, the surface may go away.
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { type CSSProperties } from 'vue'

import { FloatingLayer, useOverlayFocusReturn } from '@/shared/ui/overlay'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    /** Accessible name of the surface (visually hidden). */
    label: string
    /** Escape / outside press emit `close`. Off by default: a takeover is usually mandatory. */
    dismissible?: boolean
  }>(),
  { dismissible: false },
)

const open = defineModel<boolean>({ default: false })
const emit = defineEmits<{ close: [] }>()
const { setFocusScope } = useOverlayFocusReturn(open)

function onDialogClose(isTopmostBlocking: boolean): void {
  if (!props.dismissible || !isTopmostBlocking) return
  open.value = false
  emit('close')
}
</script>

<template>
  <FloatingLayer
    v-slot="{ style: layerStyle, layerAttributes, isTopmostBlocking }"
    kind="modal"
    :active="open"
    as="template"
  >
    <Dialog
      v-if="open"
      :open="open"
      v-bind="{ ...$attrs, ...layerAttributes }"
      class="ui-takeover fixed inset-0"
      :style="[$attrs.style as CSSProperties, layerStyle]"
      @close="onDialogClose(isTopmostBlocking)"
    >
      <div :ref="setFocusScope" class="size-full">
        <DialogPanel class="ui-takeover__panel flex size-full flex-col">
          <DialogTitle class="sr-only">{{ label }}</DialogTitle>
          <slot />
        </DialogPanel>
      </div>
    </Dialog>
  </FloatingLayer>
</template>
